import { parseAvatarPiece, type AvatarPiece } from './avatar-custom'

/** Versioned, local artwork. IDs are persisted; keep existing IDs when adding pieces. */
export const AVATAR_OPTIONS = {
  face: [
    ['round', 'Redondo'],
    ['oval', 'Oval'],
    ['square', 'Marcante'],
  ],
  body: [
    ['regular', 'Clássico'],
    ['slim', 'Delicado'],
    ['broad', 'Largo'],
  ],
  hair: [
    ['crop', 'Curtinho'],
    ['quiff', 'Topete'],
    ['side', 'De lado'],
    ['curly', 'Cachos'],
    ['afro', 'Black power'],
    ['bob', 'Chanel'],
    ['long', 'Longo'],
    ['waves', 'Ondulado'],
    ['bun', 'Coque'],
    ['ponytail', 'Rabo de cavalo'],
    ['braids', 'Tranças'],
    ['bald', 'Sem cabelo'],
  ],
  eyes: [
    ['round', 'Redondos'],
    ['almond', 'Amendoados'],
    ['bright', 'Expressivos'],
    ['smile', 'Sorridentes'],
    ['wink', 'Piscadinha'],
  ],
  brows: [
    ['soft', 'Suaves'],
    ['straight', 'Retas'],
    ['arched', 'Arqueadas'],
    ['bold', 'Marcantes'],
  ],
  mouth: [
    ['smile', 'Sorriso'],
    ['grin', 'Sorrisão'],
    ['laugh', 'Riso'],
    ['calm', 'Sereno'],
    ['kiss', 'Beijinho'],
  ],
  facialHair: [
    ['none', 'Sem barba'],
    ['stubble', 'Por fazer'],
    ['beard', 'Barba cheia'],
    ['moustache', 'Bigode'],
  ],
  clothing: [
    ['plain-tee', 'Camiseta lisa'],
    ['plain-vneck', 'Gola V lisa'],
    ['plain-long-sleeve', 'Manga longa lisa'],
    ['tee', 'Camiseta'],
    ['polo', 'Polo'],
    ['hoodie', 'Moletom'],
    ['shirt', 'Camisa'],
    ['overalls', 'Jardineira'],
    ['dress', 'Vestido'],
    ['jacket', 'Jaqueta'],
    ['hero', 'Super-herói'],
  ],
  accessory: [
    ['none', 'Sem óculos'],
    ['round-glasses', 'Redondos'],
    ['square-glasses', 'Quadrados'],
    ['sunglasses', 'De sol'],
  ],
  earrings: [
    ['none', 'Sem brincos'],
    ['stud', 'Pequenos'],
    ['hoop', 'Argolas'],
  ],
  headwear: [
    ['none', 'Sem acessório'],
    ['cap', 'Boné'],
    ['beanie', 'Gorro'],
    ['headband', 'Faixa'],
    ['crown', 'Coroa'],
    ['hijab', 'Hijab'],
  ],
  detail: [
    ['none', 'Natural'],
    ['freckles', 'Sardas'],
    ['blush', 'Bochechas rosadas'],
  ],
} as const

export const AVATAR_COLORS = {
  skin: [
    ['#FBE4CE', 'Porcelana'],
    ['#F3CDA9', 'Areia'],
    ['#EDB98A', 'Pêssego'],
    ['#DCA477', 'Dourado'],
    ['#C88C60', 'Mel'],
    ['#B87851', 'Caramelo'],
    ['#A56645', 'Canela'],
    ['#8D5238', 'Castanho'],
    ['#75432E', 'Cacau'],
    ['#603A2B', 'Café'],
    ['#4B2D24', 'Ébano'],
    ['#38251F', 'Profundo'],
  ],
  hairColor: [
    ['#282722', 'Preto'],
    ['#4A3026', 'Castanho escuro'],
    ['#79513A', 'Castanho'],
    ['#B76C35', 'Cobre'],
    ['#D6A448', 'Loiro'],
    ['#EFE0BB', 'Platinado'],
    ['#9BA5A4', 'Grisalho'],
    ['#E9EAE5', 'Branco'],
    ['#D66B97', 'Rosa'],
    ['#7964AA', 'Lilás'],
    ['#418CA4', 'Azul'],
    ['#477D62', 'Verde'],
  ],
  eyeColor: [
    ['#49362A', 'Castanho'],
    ['#232A2D', 'Preto'],
    ['#9C6C35', 'Mel'],
    ['#548266', 'Verde'],
    ['#4E88AD', 'Azul'],
    ['#8A929C', 'Cinza'],
  ],
  clothingColor: [
    ['#6FA34B', 'Verde folha'],
    ['#254D3C', 'Verde floresta'],
    ['#E9B44C', 'Mostarda'],
    ['#D57860', 'Terracota'],
    ['#CA657D', 'Rosa'],
    ['#8B77B5', 'Lavanda'],
    ['#548BAD', 'Azul'],
    ['#2D4969', 'Marinho'],
    ['#EEE9DB', 'Creme'],
    ['#343D43', 'Grafite'],
  ],
  accessoryColor: [
    ['#303A35', 'Grafite'],
    ['#A6633F', 'Tartaruga'],
    ['#D7AA4F', 'Dourado'],
    ['#D77797', 'Rosa'],
    ['#527DAB', 'Azul'],
  ],
  headwearColor: [
    ['#6FA34B', 'Verde'],
    ['#E9B44C', 'Amarelo'],
    ['#D57860', 'Coral'],
    ['#8B77B5', 'Lilás'],
    ['#548BAD', 'Azul'],
    ['#EEE9DB', 'Creme'],
    ['#343D43', 'Grafite'],
  ],
  background: [
    ['#E5EED6', 'Jardim'],
    ['#F5E6C8', 'Areia'],
    ['#F2DCD6', 'Pêssego'],
    ['#ECDCEA', 'Rosa'],
    ['#E2DDF0', 'Lavanda'],
    ['#D7E9EE', 'Céu'],
    ['#D8E9E2', 'Menta'],
    ['#E4E7EA', 'Névoa'],
  ],
} as const

type OptionMap = typeof AVATAR_OPTIONS
type ColorMap = typeof AVATAR_COLORS
export type AvatarConfig = { version: 1 } & {
  [K in keyof OptionMap]: OptionMap[K][number][0]
} & { [K in keyof ColorMap]: ColorMap[K][number][0] } & { custom?: AvatarPiece[] }
export type AvatarField = Exclude<keyof AvatarConfig, 'version' | 'custom'>
export type AvatarOption = readonly [string, string]

export const DEFAULT_AVATAR: AvatarConfig = {
  version: 1,
  face: 'round',
  body: 'regular',
  skin: '#C88C60',
  hair: 'quiff',
  hairColor: '#4A3026',
  eyes: 'round',
  eyeColor: '#49362A',
  brows: 'soft',
  mouth: 'grin',
  facialHair: 'none',
  clothing: 'tee',
  clothingColor: '#6FA34B',
  accessory: 'none',
  accessoryColor: '#303A35',
  earrings: 'none',
  headwear: 'none',
  headwearColor: '#6FA34B',
  detail: 'none',
  background: '#E5EED6',
}

export const AVATAR_PRESETS: { label: string; config: AvatarConfig }[] = [
  {
    label: 'Solar',
    config: {
      ...DEFAULT_AVATAR,
      hair: 'waves',
      skin: '#EDB98A',
      clothing: 'overalls',
      earrings: 'hoop',
      eyes: 'bright',
      clothingColor: '#D57860',
    },
  },
  {
    label: 'Essencial',
    config: {
      ...DEFAULT_AVATAR,
      hair: 'side',
      skin: '#DCA477',
      clothing: 'hoodie',
      clothingColor: '#548BAD',
    },
  },
  {
    label: 'Criativo',
    config: {
      ...DEFAULT_AVATAR,
      hair: 'afro',
      skin: '#75432E',
      hairColor: '#282722',
      accessory: 'round-glasses',
      clothing: 'shirt',
      clothingColor: '#E9B44C',
      background: '#F5E6C8',
    },
  },
  {
    label: 'Leve',
    config: {
      ...DEFAULT_AVATAR,
      hair: 'bun',
      skin: '#8D5238',
      hairColor: '#282722',
      eyes: 'almond',
      clothing: 'dress',
      clothingColor: '#8B77B5',
      background: '#E2DDF0',
    },
  },
]

export const AVATAR_CATALOG: Record<AvatarField, readonly AvatarOption[]> = {
  ...AVATAR_OPTIONS,
  ...AVATAR_COLORS,
}

/** Treat stored/API configuration as data, never as SVG, HTML, URLs or CSS. */
export function parseAvatarConfig(value: unknown): AvatarConfig | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const input = value as Record<string, unknown>
  if (input.version !== 1) return null
  for (const [field, options] of Object.entries(AVATAR_CATALOG)) {
    if (!options.some(([id]) => id === input[field])) return null
  }
  const config = Object.fromEntries(
    Object.keys(DEFAULT_AVATAR).map((key) => [key, input[key]])
  ) as AvatarConfig
  if (input.custom !== undefined) {
    if (!Array.isArray(input.custom) || input.custom.length > 19) return null
    const pieces = input.custom.map(parseAvatarPiece)
    if (pieces.some((piece) => !piece)) return null
    const custom = pieces as AvatarPiece[]
    // Ownership and availability are resolved by the server. The account collection
    // can combine global artwork with purchased pieces from different campaigns.
    if (new Set(custom.map((p) => p.category)).size !== custom.length) return null
    if (custom.length) config.custom = custom
  }
  return config
}

export function randomAvatar(): AvatarConfig {
  const values = Object.entries(AVATAR_CATALOG).map(([key, options]) => [
    key,
    options[Math.floor(Math.random() * options.length)][0],
  ])
  return { ...DEFAULT_AVATAR, ...Object.fromEntries(values) }
}
